import type { LucideIcon } from "lucide-react";
import {
  BarChart3,
  BookOpen,
  Facebook,
  Headset,
  Instagram,
  Linkedin,
  Youtube,
} from "lucide-react";

import { ShieldUserIcon } from "@/components/icons/ShieldUserIcon";
import { XIcon } from "@/components/icons/XIcon";

/**
 * Site footer link map (REK-1439).
 *
 * Canonical documentation for every footer href. The RESOURCES + FOR BUSINESSES
 * columns point at the in-app page that best matches each label.
 *
 * **URL convention — env-respecting.** All hrefs in these two columns are stored
 * as **relative paths** (`kind: "internal"`, `href: "/<path>"`). React Router
 * resolves them against the current origin, so the same link works in dev
 * (`http://localhost:8080/...`), staging (`https://beta.reklaimyou.com/...`),
 * and production (`https://www.reklaimyou.com/...`) without any per-env code.
 * That is the "SITE_URL" model: the site URL is whichever origin the browser
 * is currently on, not a hard-coded production host.
 *
 * **All six target routes are mounted in `src/App.tsx`** (`/webinars`,
 * `/protect`, `/blog/:slug`) and the underlying blog-post slugs were
 * verified to be ingested in staging Supabase (the source of truth for both
 * dev and staging), so the links resolve to real content in every env. If a
 * future ingest job regresses a slug, the article shell will render its own
 * Not-Found state inside the app — we never leak the user out to the legacy
 * site mid-session.
 *
 * When Jamie publishes a dedicated article that better matches a label, update
 * the `href` (still as a relative path) and the rationale in lock-step with the
 * table in `docs/context/FEATURE_SPECS/public-marketing-routes-and-auth.md`.
 */

export type FooterHref =
  | { kind: "internal"; href: string }
  | { kind: "external"; href: string };

export interface FooterNavLink {
  label: string;
  href: FooterHref;
}

export interface FooterColumnLink {
  label: string;
  href: FooterHref;
  /** Why this URL was chosen (for QA / content follow-up). */
  rationale: string;
}

export interface FooterColumn {
  id: "products" | "resources" | "businesses" | "support";
  title: string;
  icon: LucideIcon;
  links: FooterColumnLink[];
}

/** Top bar — primary marketing routes + social profiles. */
export const FOOTER_TOP_NAV: FooterNavLink[] = [
  { label: "How It Works", href: { kind: "internal", href: "/how-it-works" } },
  { label: "Blog", href: { kind: "internal", href: "/blog" } },
  {
    label: "Protect",
    href: { kind: "internal", href: "/protect" },
  },
  { label: "Investors", href: { kind: "internal", href: "/investors" } },
  { label: "Partners", href: { kind: "internal", href: "/partners" } },
  { label: "Help", href: { kind: "internal", href: "/help" } },
];

/**
 * Each social link carries its own icon component so the renderer in
 * `Footer.tsx` can iterate the list without branching on the `label` string.
 * Add or remove entries here -- the renderer picks them up automatically.
 */
export interface FooterSocialLink {
  label: string;
  href: string;
  icon: LucideIcon;
}

export const FOOTER_SOCIAL_LINKS: readonly FooterSocialLink[] = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/reklaim",
    icon: Linkedin,
  },
  {
    label: "X",
    href: "https://x.com/ReklaimYours",
    icon: XIcon,
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/channel/UCzupSIPz-MUwsYTTwxRsT2Q",
    icon: Youtube,
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/reklaimyours",
    icon: Instagram,
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/reklaimyours",
    icon: Facebook,
  },
] as const;

export const FOOTER_COLUMNS: FooterColumn[] = [
  {
    id: "products",
    title: "PRODUCTS",
    icon: ShieldUserIcon,
    links: [
      {
        label: "Data Removal & Privacy Protection",
        href: { kind: "external", href: "https://protect.reklaimyours.com" },
        rationale:
          "Interim: link to the external Reklaim Protect product site. Repoint to `/protect` once the in-app marketing page is ready.",
      },
      {
        label: "Earn Rewards for Your Data",
        href: { kind: "internal", href: "/earn" },
        rationale:
          "Earn marketing page — distinct from How It Works; focused on participating, earning entries, and winning cash.",
      },
      {
        label: "Consumer Data Marketplace",
        href: { kind: "internal", href: "/partners" },
        rationale:
          "Partners marketing page — the public-facing overview of Reklaim's consumer data marketplace for business partners.",
      },
    ],
  },
  {
    id: "resources",
    title: "RESOURCES",
    icon: BookOpen,
    links: [
      {
        label: "What is Consumer Data Privacy",
        href: { kind: "internal", href: "/webinars" },
        rationale:
          "`/webinars` (Privacy & Data Webinars hub) — Jamie's preferred destination for an evergreen 'what is consumer data privacy' read. The hub curates expert-led webinars covering data brokers, state privacy laws (CPRA, Delete Act, MyHealthMyData), zero-party data, and consent — the broadest single answer to 'what is consumer data privacy' currently on the site.",
      },
      {
        label: "How Data Brokers Work",
        href: { kind: "internal", href: "/protect" },
        rationale:
          "`/protect` (Reklaim Protect product page) — the canonical site location describing the data broker ecosystem. Page sections cover 'Monthly Data Broker Removals' (600+ brokers contacted monthly) and a 3-step 'Here's How It Works' flow that walks consumers through how Reklaim scans hundreds of data broker sites, requests removal, and monitors for re-listing. Closest single-page answer to 'how do data brokers work?' on the public site.",
      },
      {
        label: "Privacy Laws & Regulations",
        href: { kind: "internal", href: "/blog/spi-opt-in" },
        rationale:
          "Article 'The Growing Importance of Opt-In Consent for Processing Sensitive Personal Information (SPI)' — covers 19 US state opt-in laws (California, Colorado, Connecticut, Iowa, Indiana, Montana, Tennessee, Texas, Utah, Virginia, Oregon, NJ, NH, Delaware, Nebraska, Kentucky, Maryland, Minnesota, Rhode Island), proposed bills in HI/MA/NY/PA, and the consumer-population impact (~97M).",
      },
    ],
  },
  {
    id: "businesses",
    title: "FOR BUSINESSES",
    icon: BarChart3,
    links: [
      {
        label: "Multicultural Data Segments",
        href: {
          kind: "internal",
          href: "/blog/navigating-the-new-rules-of-marketing-to-multicultural-audiences",
        },
        rationale:
          "Article 'Navigating the New Rules of Marketing to Multicultural Audiences' by CEO Neil Sweeney (AdMonsters) — explores how brands can reach diverse audiences using zero-party data in a privacy-first market.",
      },
      {
        label: "Programmatic Data Activation",
        href: {
          kind: "internal",
          href: "/blog/seasonal-audiences-q3",
        },
        rationale:
          "Article 'Drive Campaigns Q3 & Beyond With Reklaim's Seasonal Opt-In Data & DealIDs' — programmatic + social activation guide for Q3 (Pride, Festivals, Live Sports, Back-to-School, Olympics) across Display, OLV, CTV, YouTube, and Search in the US + Canada.",
      },
      {
        label: "Deal IDs & Data Monetization",
        href: {
          kind: "internal",
          href: "/blog/introducing-reklaims-opt-in-dealid-marketplace",
        },
        rationale:
          "Article 'Introducing Reklaim's Opt-In DealID Marketplace' — overview of 300+ pre-built DealIDs across Health, Political, Multicultural, B2B, HHI, Parents, and demographic categories. Includes the consumer-centric monetization story and DSP/SSP-agnostic activation model.",
      },
    ],
  },
  {
    id: "support",
    title: "SUPPORT",
    icon: Headset,
    links: [
      {
        label: "Help Center",
        href: { kind: "internal", href: "/help" },
        rationale: "Ruby-powered help center.",
      },
      {
        label: "Contact Us",
        href: { kind: "internal", href: "/contact-us" },
        rationale: "General contact form (preserved from legacy footer).",
      },
    ],
  },
];

export const FOOTER_LEGAL_LINKS: FooterNavLink[] = [
  { label: "Privacy", href: { kind: "internal", href: "/privacy" } },
  { label: "Terms", href: { kind: "internal", href: "/terms" } },
  { label: "Do Not Sell", href: { kind: "internal", href: "/do-not-sell" } },
  { label: "Delete Account", href: { kind: "internal", href: "/delete-my-account" } },
];

/**
 * Ordered bottom legal-bar items. The renderer iterates this list directly and
 * the visual `|` separators are inserted between items by the renderer (no
 * hard-coded `slice` / index lookups). To re-order, add, or remove the
 * Cookie Settings control, edit this list — the renderer does NOT special-case
 * any individual item by label or index.
 *
 * The discriminated union (`kind: "link" | "cookieSettings"`) lets us mix the
 * `<CookiePreferencesButton>` action into the same flow as the standard
 * `<FooterAnchor>` links without forking the rendering loop.
 */
export type FooterLegalBarItem =
  | { kind: "link"; label: string; href: FooterHref }
  | { kind: "cookieSettings"; label: string };

export const FOOTER_LEGAL_BAR_ITEMS: FooterLegalBarItem[] = [
  { kind: "cookieSettings", label: "Cookie Settings" },
  { kind: "link", label: "Privacy", href: { kind: "internal", href: "/privacy" } },
  { kind: "link", label: "Terms", href: { kind: "internal", href: "/terms" } },
  // REK-1704: Protect Terms, Financial Incentive Notice, and Data Broker
  // Notice were surfaced from the (now-removed) per-legal-page related-links
  // strip / Privacy Policy body so the footer is the single home for every
  // legal document.
  { kind: "link", label: "Protect Terms", href: { kind: "internal", href: "/protect-terms" } },
  {
    kind: "link",
    label: "Financial Incentive Notice",
    href: { kind: "internal", href: "/notice-of-incentive" },
  },
  {
    kind: "link",
    label: "Data Broker Notice",
    href: { kind: "internal", href: "/data-broker-privacy-notice" },
  },
  { kind: "link", label: "Do Not Sell", href: { kind: "internal", href: "/do-not-sell" } },
  {
    kind: "link",
    label: "Delete Account",
    href: { kind: "internal", href: "/delete-my-account" },
  },
];

export const FOOTER_TICKERS = ["TSXV: MYID", "OTC: MYIDF"] as const;
